import React from "react";
import { connect } from "react-redux";
import { Badge, Col, Form, Row } from "react-bootstrap";

import ConditionalButton from "../../../layout/conditional-button";
import { get as getSetting, save as saveSetting } from "../../../actions/settings";
import { loading, send as pageInfo } from "../../../actions/page";
import { pack as sendAlert } from "../../../actions/alert";

const PERSON_TYPES = [
  { key: "person", label: "Person" },
  { key: "lead", label: "Lead" },
  { key: "client", label: "Client" },
  { key: "vendor", label: "Vendor" },
  { key: "attorney", label: "Attorney" },
  { key: "staff", label: "Staff" },
  { key: "judge", label: "Judge" },
];

const DEFAULT_COLUMNS = [
  { key: "NOT_STARTED", label: "Not Started", terminal: false },
  { key: "STARTED", label: "Started", terminal: false },
  { key: "CANCELLED", label: "Cancelled", terminal: true },
  { key: "FINISHED", label: "Finished", terminal: true },
];

const DEFAULT_SETTINGS = {
  calendarMirror: true,
  types: Object.fromEntries(PERSON_TYPES.map((type) => [type.key, { columns: DEFAULT_COLUMNS }])),
};

const clone = (value) => JSON.parse(JSON.stringify(value || {}));

const safeParseJson = (value, fallback = {}) => {
  try {
    return typeof value === "string" ? JSON.parse(value) : (value || fallback);
  } catch (_err) {
    return fallback;
  }
};

const normalizeKey = (value, fallback) => {
  const key = String(value || "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return key || fallback;
};

const normalizeColumns = (columns) => {
  const seen = new Set();
  const items = (Array.isArray(columns) && columns.length ? columns : DEFAULT_COLUMNS)
    .map((column, index) => {
      const key = normalizeKey(column.key || column.label, DEFAULT_COLUMNS[index]?.key || `COLUMN_${index + 1}`);
      const uniqueKey = seen.has(key) ? `${key}_${index + 1}` : key;
      seen.add(uniqueKey);
      return {
        key: uniqueKey,
        label: String(column.label || uniqueKey).trim() || uniqueKey,
        terminal: Boolean(column.terminal),
      };
    });
  return items.length ? items : clone(DEFAULT_COLUMNS);
};

const mergeSettings = (value) => {
  const raw = value || {};
  const types = {};
  PERSON_TYPES.forEach((type) => {
    types[type.key] = {
      ...(raw.types?.[type.key] || {}),
      columns: normalizeColumns(raw.types?.[type.key]?.columns),
    };
  });
  return {
    calendarMirror: raw.calendarMirror == null ? true : Boolean(raw.calendarMirror),
    types,
  };
};

class BoardSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      setting: null,
      selectedType: "lead",
      boards: clone(DEFAULT_SETTINGS),
    };
  }

  componentDidMount = async () => {
    await this.props.pageInfo({ title: "Board Settings", breadcrumb: "Boards" });
    await this.loadData();
  };

  loadData = async () => {
    this.props.loading(true);
    try {
      const setting = await this.props.getSetting({ authentic: true, name: "BOARDS" });
      this.setState({
        setting,
        boards: mergeSettings(safeParseJson(setting?.value, DEFAULT_SETTINGS)),
      });
    } finally {
      this.props.loading(false);
    }
  };

  setColumns = (type, columns) => {
    this.setState((prev) => ({
      boards: {
        ...prev.boards,
        types: {
          ...prev.boards.types,
          [type]: {
            ...(prev.boards.types[type] || {}),
            columns,
          },
        },
      },
    }));
  };

  onColumnChange = (index, field, value) => {
    const type = this.state.selectedType;
    const columns = [...(this.state.boards.types[type]?.columns || DEFAULT_COLUMNS)];
    columns[index] = {
      ...columns[index],
      [field]: field === "terminal" ? Boolean(value) : value,
    };
    this.setColumns(type, columns);
  };

  onAddColumn = () => {
    const type = this.state.selectedType;
    const columns = [...(this.state.boards.types[type]?.columns || DEFAULT_COLUMNS)];
    columns.push({ key: `COLUMN_${columns.length + 1}`, label: `Column ${columns.length + 1}`, terminal: false });
    this.setColumns(type, columns);
  };

  onRemoveColumn = (index) => {
    const type = this.state.selectedType;
    const columns = (this.state.boards.types[type]?.columns || DEFAULT_COLUMNS).filter((_, i) => i !== index);
    this.setColumns(type, columns);
  };

  onMoveColumn = (index, direction) => {
    const type = this.state.selectedType;
    const columns = [...(this.state.boards.types[type]?.columns || DEFAULT_COLUMNS)];
    const target = index + direction;
    if (target < 0 || target >= columns.length) return;
    [columns[index], columns[target]] = [columns[target], columns[index]];
    this.setColumns(type, columns);
  };

  onCopyFromPerson = () => {
    const columns = clone(this.state.boards.types.person?.columns || DEFAULT_COLUMNS);
    this.setColumns(this.state.selectedType, columns);
  };

  onSaveClick = async () => {
    const boards = mergeSettings(this.state.boards);
    this.props.loading(true);
    try {
      const result = await this.props.saveSetting({
        uuid: this.state.setting?.uuid,
        name: "BOARDS",
        category: "Services",
        description: "Person board column and calendar settings",
        visibility: this.state.setting?.visibility ?? 3,
        priority: this.state.setting?.priority ?? 0,
        value: JSON.stringify(boards),
      });
      this.setState({ setting: result, boards });
      this.props.sendAlert(["Board settings saved."], "success");
    } finally {
      this.props.loading(false);
    }
  };

  renderColumn = (column, index, columns) => (
    <Col xs="12" key={`${this.state.selectedType}.${index}`}>
      <Row className="align-items-center g-3 border-bottom pb-3 mb-3">
        <Col xs="12" md="3">
          <div className="top-label">
            <Form.Control
              type="text"
              value={column.key || ""}
              onChange={(event) => this.onColumnChange(index, "key", event.target.value)}
            />
            <Form.Label>KEY</Form.Label>
          </div>
        </Col>
        <Col xs="12" md="4">
          <div className="top-label">
            <Form.Control
              type="text"
              value={column.label || ""}
              onChange={(event) => this.onColumnChange(index, "label", event.target.value)}
            />
            <Form.Label>LABEL</Form.Label>
          </div>
        </Col>
        <Col xs="12" md="2">
          <Form.Check
            type="switch"
            id={`board.${this.state.selectedType}.${index}.terminal`}
            label="Terminal"
            checked={Boolean(column.terminal)}
            onChange={(event) => this.onColumnChange(index, "terminal", event.target.checked)}
          />
        </Col>
        <Col xs="12" md="3" className="text-md-end">
          <button type="button" className="btn btn-link btn-sm p-0 me-3" disabled={index === 0} onClick={() => this.onMoveColumn(index, -1)}>
            Up
          </button>
          <button type="button" className="btn btn-link btn-sm p-0 me-3" disabled={index === columns.length - 1} onClick={() => this.onMoveColumn(index, 1)}>
            Down
          </button>
          <button type="button" className="btn btn-link btn-sm p-0" disabled={columns.length <= 1} onClick={() => this.onRemoveColumn(index)}>
            Remove
          </button>
        </Col>
      </Row>
    </Col>
  );

  render() {
    const selected = PERSON_TYPES.find((type) => type.key === this.state.selectedType) || PERSON_TYPES[0];
    const columns = this.state.boards.types?.[selected.key]?.columns || DEFAULT_COLUMNS;

    return (
      <>
        <Col md="8" className="float-right text-end">
          <ConditionalButton enabledVariant="outline-primary" tooltip="Save Board Settings" display enabled onEnabledClick={this.onSaveClick} icon="save">
            Save
          </ConditionalButton>
        </Col>

        <Col xs="12">
          <Row className="g-3">
            <Col xs="12" md="4">
              <Form.Check
                type="switch"
                id="boards.calendarMirror"
                label="Create calendar events for task dates"
                checked={Boolean(this.state.boards.calendarMirror)}
                onChange={(event) => this.setState((prev) => ({ boards: { ...prev.boards, calendarMirror: event.target.checked } }))}
              />
            </Col>

            <Col xs="12">
              <h2 className="small-title">Person Types</h2>
              <div className="d-flex flex-wrap gap-2">
                {PERSON_TYPES.map((type) => (
                  <button
                    key={type.key}
                    type="button"
                    className={`btn btn-sm ${this.state.selectedType === type.key ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => this.setState({ selectedType: type.key })}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </Col>

            <Col xs="12" className="mt-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h2 className="small-title mb-0">{selected.label} Columns</h2>
                  <Badge bg="light" text="dark">{columns.length}</Badge>
                </div>
                <div>
                  <button type="button" className="btn btn-outline-primary btn-sm me-2" onClick={this.onCopyFromPerson}>
                    Copy Person
                  </button>
                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={this.onAddColumn}>
                    Add Column
                  </button>
                </div>
              </div>
            </Col>
            {columns.map((column, index) => this.renderColumn(column, index, columns))}
          </Row>
        </Col>
      </>
    );
  }
}

export default connect(null, {
  getSetting,
  saveSetting,
  loading,
  pageInfo,
  sendAlert,
})(BoardSettings);
